import { useState } from "react";
import { Copy, Check, ExternalLink } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { Badge } from "../client/components/ui/badge";
import { Button } from "../client/components/ui/button";
import { Checkbox } from "../client/components/ui/checkbox";
import type { OfferVM, FirmVM } from "./lib/types";
import { isPromoActive, effectivePrice, totalCost } from "./lib/derive";
import { buildAffiliateUrl } from "./lib/affiliate";
import { formatUsd } from "./lib/format";
import { drawdownTypeLabel, ratingClass } from "./lib/labels";

interface OfferRowProps {
  offer: OfferVM;
  firm: FirmVM;
  now: Date;
  isSelected: boolean;
  onToggleCompare: (offerId: string) => void;
}

export default function ComparatorOfferRow({
  offer,
  firm,
  now,
  isSelected,
  onToggleCompare,
}: OfferRowProps) {
  const t = useTranslation();
  const [copied, setCopied] = useState(false);

  const promo = isPromoActive(offer, now);
  const eff = effectivePrice(offer, now);
  const url = buildAffiliateUrl(offer, firm);

  const copyPromoCode = async () => {
    if (!firm.promoCode) return;
    try {
      await navigator.clipboard.writeText(firm.promoCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — silently ignore */
    }
  };

  return (
    <tr className="border-b border-border hover:bg-accent/40">
      {/* 1. Compare + firm */}
      <td className="py-3 pr-3 align-top">
        <div className="flex items-start gap-3">
          <Checkbox
            className="mt-1"
            checked={isSelected}
            onCheckedChange={() => onToggleCompare(offer.id)}
          />
          <div className="min-w-0">
            <div className="font-medium text-foreground">{firm.name}</div>
            <div className="text-sm text-muted-foreground">{offer.planName}</div>
            <div className="mt-1 flex items-center gap-2">
              {firm.countryCode && (
                <Badge variant="outline" className="uppercase text-[10px]">
                  {firm.countryCode}
                </Badge>
              )}
              {firm.foundedYear != null && (
                <span className="text-xs text-muted-foreground font-mono">
                  {firm.foundedYear}
                </span>
              )}
            </div>
          </div>
        </div>
      </td>

      {/* 2. Size */}
      <td className="py-3 px-3 align-top">
        <Badge variant="outline" className="font-mono">
          {offer.accountSizeK}k
        </Badge>
      </td>

      {/* 3. Price */}
      <td className="py-3 px-3 align-top">
        {promo ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-mono text-base font-semibold text-foreground">
                {formatUsd(eff)}
              </span>
              <Badge className="bg-emerald-500/15 text-emerald-500">
                -{offer.discountPercent}%
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="line-through text-muted-foreground font-mono text-xs">
                {formatUsd(offer.priceUsd)}
              </span>
              {firm.promoCode && (
                <button
                  type="button"
                  onClick={copyPromoCode}
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  {copied ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                  {copied ? t.comparator.codeCopied : t.comparator.copyCode}
                </button>
              )}
            </div>
            {offer.discountExpiresAt && (
              <div className="text-xs text-muted-foreground">
                {t.comparator.promoExpires.replace(
                  "{date}",
                  new Date(offer.discountExpiresAt).toLocaleDateString(),
                )}
              </div>
            )}
          </div>
        ) : (
          <span className="font-mono text-foreground">
            {formatUsd(offer.priceUsd)}
          </span>
        )}
      </td>

      {/* 4. Billing */}
      <td className="py-3 px-3 align-top text-sm text-muted-foreground">
        {offer.billingType === "one_time"
          ? t.comparator.billingOneTime
          : t.comparator.billingMonthly}
      </td>

      {/* 5. Activation */}
      <td className="py-3 px-3 align-top">
        {offer.activationFeeUsd === 0 ? (
          <span className="font-mono text-emerald-500">{t.comparator.free}</span>
        ) : (
          <div>
            <span className="font-mono text-foreground">
              {formatUsd(offer.activationFeeUsd)}
            </span>
            <div className="text-xs text-muted-foreground font-mono">
              {t.comparator.total}: {formatUsd(totalCost(offer, now))}
            </div>
          </div>
        )}
      </td>

      {/* 6. Platforms */}
      <td className="py-3 px-3 align-top">
        <div className="flex flex-wrap gap-1">
          {offer.platforms.map((p) => (
            <Badge key={p} variant="outline" className="text-[10px]">
              {p}
            </Badge>
          ))}
        </div>
      </td>

      {/* 7. Drawdown */}
      <td className="py-3 px-3 align-top">
        {offer.drawdownUsd != null ? (
          <div className="flex flex-col gap-1">
            <span className="font-mono text-foreground">
              {formatUsd(offer.drawdownUsd)}
            </span>
            {offer.drawdownType && (
              <Badge variant="outline" className="w-fit text-[10px]">
                {drawdownTypeLabel(t, offer.drawdownType)}
              </Badge>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>

      {/* 8. Target */}
      <td className="py-3 px-3 align-top">
        {offer.profitTargetUsd != null ? (
          <span className="font-mono text-foreground">
            {formatUsd(offer.profitTargetUsd)}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>

      {/* 9. Round trip */}
      <td className="py-3 px-3 align-top">
        <span className="font-mono text-foreground">
          $
          {offer.roundTripMicroUsd != null
            ? offer.roundTripMicroUsd.toLocaleString("en-US")
            : "—"}{" "}
          / $
          {offer.roundTripMiniUsd != null
            ? offer.roundTripMiniUsd.toLocaleString("en-US")
            : "—"}
        </span>
      </td>

      {/* 10. Rating */}
      <td className="py-3 px-3 align-top">
        <span className={`font-mono ${ratingClass(firm.rating)}`}>
          {firm.rating ?? "—"}
        </span>
      </td>

      {/* 11. Action */}
      <td className="py-3 pl-3 align-top">
        {url ? (
          <a href={url} target="_blank" rel="sponsored nofollow noopener noreferrer">
            <Button size="sm">
              {t.comparator.startChallenge}
              <ExternalLink className="ml-1 h-3 w-3" />
            </Button>
          </a>
        ) : (
          <Button size="sm" disabled>
            {t.comparator.startChallenge}
          </Button>
        )}
      </td>
    </tr>
  );
}
